/**
 * In-app knowledge base for operators. Each article maps to a real EMS module.
 * `relatedLinks` use in-app routes only (admin shell).
 */

function article(id, title, summary, sections, relatedLinks = []) {
  return { id, title, summary, sections, relatedLinks };
}

export const HELP_TABS = [
  { id: "kb", label: "Knowledge base" },
  { id: "chat", label: "AI assistant" },
  { id: "support", label: "Support" },
  { id: "feature", label: "Feature request" },
];

export const HELP_MODULE_OPTIONS = [
  { value: "general", label: "General / not sure" },
  { value: "dashboard", label: "Dashboard & overview" },
  { value: "events", label: "Events" },
  { value: "attendees", label: "Attendees" },
  { value: "tickets", label: "Tickets & registration" },
  { value: "communications", label: "Communications" },
  { value: "landing", label: "Page builder & public sites" },
  { value: "form", label: "Form builder" },
  { value: "schedule", label: "Schedule & sessions" },
  { value: "badge", label: "Badge designer" },
  { value: "wallet", label: "Wallet pass" },
  { value: "checkin", label: "Live check-in & onsite" },
  { value: "reports", label: "Reports & analytics" },
  { value: "finance", label: "Finance" },
  { value: "media", label: "Media library" },
  { value: "settings", label: "Settings & team" },
  { value: "billing", label: "Billing & plans" },
  { value: "api", label: "API keys & webhooks" },
  { value: "notifications", label: "Notifications" },
  { value: "audit", label: "Audit log" },
  { value: "profile", label: "Profile & workspaces" },
];

export const HELP_CATEGORIES = [
  {
    id: "start",
    title: "Getting started",
    description: "Sign-in, navigation, and workspace basics.",
    articles: [
      article(
        "console-overview",
        "Admin console overview",
        "How the workspace shell is organized and where to find core actions.",
        [
          {
            heading: "Top navigation",
            body: [
              "Overview shows KPIs and active events.",
              "Events lists every program in the workspace.",
              "Finance, Registrations, Audit, and Settings are workspace-wide.",
              "Use the global search bar (⌘K) to jump to events, attendees, or routes.",
            ],
          },
          {
            heading: "Event scope",
            body: [
              "Open an event from Events to unlock event-specific modules: Attendees, Tickets, Comms, Page Builder, Check-in, and more.",
              "Breadcrumbs in the sidebar reflect whether you are in workspace or event context.",
            ],
          },
        ],
        [{ label: "Dashboard", to: "/admin" }, { label: "Events", to: "/admin/events" }],
      ),
      article(
        "sign-in-security",
        "Sign in, sessions, and password recovery",
        "Credentials, SSO, and what to do when a session expires.",
        [
          {
            heading: "Password sign-in",
            body: [
              "Use your workspace email and password at /login.",
              "If your session expires, sign in again — you return to the admin console after authentication.",
            ],
          },
          {
            heading: "SSO",
            body: [
              "When SSO is Active under Settings → Security, the login page shows Continue with SSO.",
              "OIDC issuer and status must be configured by an admin before SSO appears.",
            ],
          },
          {
            heading: "Forgot password",
            body: [
              "Use Forgot? on the login page or /security for the reset flow.",
              "Reset links expire after one hour; request a new link if needed.",
            ],
          },
        ],
        [{ label: "Security settings", to: "/admin/settings" }],
      ),
      article(
        "workspaces-profile",
        "Profile & multiple workspaces",
        "Switch between accounts when you belong to more than one organization.",
        [
          {
            heading: "Switch workspace",
            body: [
              "Open Profile & workspaces from the account menu.",
              "Each membership shows the workspace name and your role.",
              "Switching issues a new session for that tenant and reloads branding.",
            ],
          },
          {
            heading: "Roles",
            body: [
              "Owner has full control including billing and ownership transfer.",
              "Admin, Marketing, Finance, and Onsite roles limit capabilities — see Settings → Team for the matrix.",
            ],
          },
        ],
        [{ label: "Profile", to: "/admin/profile" }],
      ),
    ],
  },
  {
    id: "events",
    title: "Events",
    description: "Create programs, configure basics, and manage lifecycle.",
    articles: [
      article(
        "create-event",
        "Create a new event",
        "Use the event wizard to stand up registration, branding, and operations.",
        [
          {
            heading: "Wizard steps",
            body: [
              "From Events, click New event and complete basics: name, dates, venue, timezone, and branding.",
              "Ticket tiers and registration windows can be defined during setup or later under Tickets.",
              "Publishing controls when the public landing and registration open.",
            ],
          },
        ],
        [{ label: "New event", to: "/admin/events/new" }, { label: "Events list", to: "/admin/events" }],
      ),
      article(
        "event-basics",
        "Event basics & branding",
        "Dates, venue, cover image, custom domain, and public access rules.",
        [
          {
            heading: "Basics manager",
            body: [
              "Set display timezone — used for schedules, emails, and reports.",
              "Upload a cover image from Media or an external URL; it appears on the public landing.",
              "Custom domains point a hostname at this event (Scale plan feature), e.g. events.example.com — no https:// or path.",
            ],
          },
          {
            heading: "Demo vs live",
            body: [
              "Demo events are sandboxed for training; live events accept real registrations.",
              "Status (draft, selling, live, ended) controls attendee-facing messaging.",
            ],
          },
        ],
        [{ label: "Event basics (pick an event first)", to: "/admin/events" }],
      ),
      article(
        "event-overview",
        "Event overview & navigation",
        "Per-event dashboard and module shortcuts.",
        [
          {
            heading: "Overview tab",
            body: [
              "Shows registration totals, check-in progress, and quick links.",
              "Use the left event nav to jump to Attendees, Communications, Check-in, etc.",
            ],
          },
        ],
        [{ label: "Events", to: "/admin/events" }],
      ),
    ],
  },
  {
    id: "people",
    title: "Attendees & registration",
    description: "Guest lists, imports, forms, and tickets.",
    articles: [
      article(
        "attendee-list",
        "Attendee list & spreadsheet",
        "Search, filter, edit, and bulk-manage registrants.",
        [
          {
            heading: "List view",
            body: [
              "Sort by name, email, status, ticket, or check-in time.",
              "Inline edits save per row; use filters for ticket type or check-in state.",
            ],
          },
          {
            heading: "CSV import",
            body: [
              "Import wizard maps columns to standard and custom fields.",
              "Dedupe dialog resolves conflicts on email before rows are created.",
            ],
          },
        ],
        [{ label: "Attendees (open an event)", to: "/admin/events" }],
      ),
      article(
        "form-builder",
        "Registration form builder",
        "Custom questions, validation, and field library.",
        [
          {
            heading: "Fields",
            body: [
              "Drag fields from the library or create new ones (text, email, select, file, etc.).",
              "Mark fields required and add help text shown on the public form.",
              "Conditional logic can be added for advanced flows (Studio+).",
            ],
          },
        ],
        [{ label: "Form builder", to: "/admin/events" }],
      ),
      article(
        "tickets",
        "Tickets & pricing",
        "Tiers, quotas, promo codes, and add-ons.",
        [
          {
            heading: "Ticket tiers",
            body: [
              "Define price, quota, and sale window per tier.",
              "Track sold vs quota on the Tickets page and event overview.",
            ],
          },
          {
            heading: "Promo codes",
            body: [
              "Percentage or fixed discounts with optional usage limits.",
              "Codes apply at checkout on the public registration page.",
            ],
          },
        ],
        [{ label: "Tickets", to: "/admin/events" }],
      ),
      article(
        "registrations-report",
        "Workspace registrations report",
        "Cross-event registration export and filters.",
        [
          {
            heading: "Report",
            body: [
              "Filter by event, date range, or ticket type.",
              "Export CSV for finance or marketing handoff.",
            ],
          },
        ],
        [{ label: "Registrations", to: "/admin/reports/registrations" }],
      ),
    ],
  },
  {
    id: "marketing",
    title: "Communications & public sites",
    description: "Email campaigns, landings, and attendee-facing pages.",
    articles: [
      article(
        "communications",
        "Email campaigns",
        "Send invitations, reminders, and segmented mailings.",
        [
          {
            heading: "Campaigns",
            body: [
              "Create a campaign, choose a template, and define the audience segment.",
              "Preview sends and track opens/clicks where tracking is enabled.",
              "Email credits depend on your plan — see Billing for allowance.",
            ],
          },
          {
            heading: "Deliverability",
            body: [
              "Use verified sender domains configured in workspace settings.",
              "Avoid sudden large blasts on new domains; warm up with smaller batches.",
            ],
          },
        ],
        [{ label: "Communications", to: "/admin/events" }],
      ),
      article(
        "page-builder",
        "Public landing (page builder)",
        "Marketing pages, agenda blocks, and speakers.",
        [
          {
            heading: "Sections",
            body: [
              "Compose hero, agenda, speakers, sponsors, and FAQ blocks.",
              "Changes publish to /e/{slug} or your custom domain (e.g. events.example.com) when configured.",
            ],
          },
        ],
        [{ label: "Page builder", to: "/admin/events" }],
      ),
      article(
        "schedule",
        "Schedule & sessions",
        "Agenda items, tracks, and speaker assignments.",
        [
          {
            heading: "Sessions",
            body: [
              "Add sessions with start/end times in the event timezone.",
              "Link speakers from the roster; sessions can surface on the landing and wallet pass.",
            ],
          },
        ],
        [{ label: "Schedule", to: "/admin/events" }],
      ),
    ],
  },
  {
    id: "onsite",
    title: "Onsite & credentials",
    description: "Check-in, badges, wallet passes, and door operations.",
    articles: [
      article(
        "live-checkin",
        "Live check-in",
        "Scan QR codes, search guests, and monitor throughput.",
        [
          {
            heading: "Check-in desk",
            body: [
              "Use camera scan or manual search by name, email, or confirmation code.",
              "Undo check-in if someone was scanned by mistake.",
              "Feed shows recent activity for supervisors.",
            ],
          },
        ],
        [{ label: "Live check-in", to: "/admin/events" }],
      ),
      article(
        "onsite-mode",
        "Onsite app mode",
        "Simplified UI for staff devices at the venue.",
        [
          {
            heading: "Onsite routes",
            body: [
              "Open /onsite from a tablet for a focused check-in experience.",
              "Requires the same operator session as the admin console.",
            ],
          },
        ],
        [{ label: "Onsite", to: "/onsite" }],
      ),
      article(
        "badge-designer",
        "Badge designer",
        "Print layouts, fields, and test prints.",
        [
          {
            heading: "Templates",
            body: [
              "Place dynamic fields (name, company, QR) on the canvas.",
              "Print test badges before the event day; align with label stock dimensions.",
            ],
          },
        ],
        [{ label: "Badge", to: "/admin/events" }],
      ),
      article(
        "wallet-pass",
        "Apple & Google Wallet passes",
        "Digital tickets attendees add to their phone.",
        [
          {
            heading: "Configuration",
            body: [
              "Set colors, logo, and fields shown on the pass.",
              "Attendees receive a wallet link after registration or via email.",
            ],
          },
        ],
        [{ label: "Wallet pass", to: "/admin/events" }],
      ),
    ],
  },
  {
    id: "insights",
    title: "Reports & finance",
    description: "Analytics, exports, and money movement.",
    articles: [
      article(
        "event-reports",
        "Event reports",
        "Attendance, funnel, and session metrics per program.",
        [
          {
            heading: "Dashboards",
            body: [
              "Registration funnel, check-in rate, and tier breakdown.",
              "Export snapshots for steering meetings.",
            ],
          },
        ],
        [{ label: "Reports", to: "/admin/events" }],
      ),
      article(
        "finance",
        "Finance workspace",
        "Orders, refunds, and reconciliation across events.",
        [
          {
            heading: "Finance module",
            body: [
              "View paid orders and refund requests.",
              "Finance role can issue refunds; owners and admins configure gateways under Settings.",
            ],
          },
        ],
        [{ label: "Finance", to: "/admin/finance" }],
      ),
      article(
        "audit",
        "Audit log",
        "Who changed what, and when.",
        [
          {
            heading: "Audit trail",
            body: [
              "Workspace and event actions are logged with actor and timestamp.",
              "Useful for compliance and debugging team changes.",
            ],
          },
        ],
        [{ label: "Audit", to: "/admin/audit" }],
      ),
    ],
  },
  {
    id: "workspace",
    title: "Workspace settings",
    description: "Team, brand, security, integrations, and billing.",
    articles: [
      article(
        "team-roles",
        "Team & roles",
        "Invite operators and control permissions.",
        [
          {
            heading: "Invites",
            body: [
              "Send email invites; recipients set a password via the link.",
              "Pending invites can be resent or revoked before acceptance.",
            ],
          },
          {
            heading: "Permission matrix",
            body: [
              "Owner: full access. Admin: most modules. Marketing: comms. Onsite: check-in. Finance: money modules.",
            ],
          },
        ],
        [{ label: "Team settings", to: "/admin/settings" }],
      ),
      article(
        "organization",
        "Organization & ownership",
        "Legal entity, tax, timezone, and ownership transfer.",
        [
          {
            heading: "Organization tab",
            body: [
              "Workspace name, currency, and display timezone apply across reports.",
              "Tax ID and rate feed registration checkout where configured.",
            ],
          },
          {
            heading: "Transfer ownership",
            body: [
              "Owners can nominate a team member; they must accept via email before becoming owner.",
              "Previous owner becomes an admin after acceptance.",
            ],
          },
        ],
        [{ label: "Organization", to: "/admin/settings" }],
      ),
      article(
        "whitelabel",
        "White-label branding",
        "Logo, colors, fonts, and custom CSS on admin and public surfaces.",
        [
          {
            heading: "Brand kit",
            body: [
              "Enable branding to override primary colors and product name.",
              "Custom CSS is scoped — test on staging before production.",
              "Available on plans with whitelabel entitlement.",
            ],
          },
        ],
        [{ label: "White-label", to: "/admin/settings" }],
      ),
      article(
        "security-sso",
        "Security & SSO",
        "2FA policy, OIDC, and connected accounts.",
        [
          {
            heading: "SSO setup",
            body: [
              "Set OIDC issuer and set status to Active.",
              "Test with a non-production IdP before enforcing for all users.",
            ],
          },
          {
            heading: "Social sign-in",
            body: [
              "Link Google or Apple under Security for passwordless login where enabled.",
            ],
          },
        ],
        [{ label: "Security", to: "/admin/settings" }],
      ),
      article(
        "api-webhooks",
        "API keys & webhooks",
        "Integrate registration and check-in with external systems.",
        [
          {
            heading: "API keys",
            body: [
              "Create scoped keys under Settings → API or the API keys page.",
              "Use keys for server-to-server exports — never embed in public sites.",
            ],
          },
          {
            heading: "Webhooks",
            body: [
              "Subscribe to registration and check-in events.",
              "Test endpoints and review delivery logs after configuration.",
            ],
          },
        ],
        [{ label: "API keys", to: "/admin/api-keys" }, { label: "Integrations", to: "/admin/settings" }],
      ),
      article(
        "billing-plans",
        "Billing & subscription",
        "Plans, Revolut checkout, and email credits.",
        [
          {
            heading: "Self-serve",
            body: [
              "Starter, Studio, Scale, and Enterprise tiers unlock feature flags.",
              "Annual SGD billing syncs through Revolut when configured.",
            ],
          },
          {
            heading: "Usage",
            body: [
              "Track events per year, admin seats, and email credits on the Billing page.",
            ],
          },
        ],
        [{ label: "Billing", to: "/admin/billing" }],
      ),
      article(
        "media-library",
        "Media library",
        "Shared images and files for events and brand.",
        [
          {
            heading: "Uploads",
            body: [
              "Upload once, reuse across event covers, emails, and page builder.",
              "Supported formats include PNG, JPG, and SVG where noted.",
            ],
          },
        ],
        [{ label: "Media", to: "/admin/media" }],
      ),
      article(
        "notifications",
        "Notifications inbox",
        "In-app alerts for registrations, refunds, and system events.",
        [
          {
            heading: "Inbox",
            body: [
              "Configure channels under Settings → Notifications.",
              "Mark items read and follow links to the relevant module.",
            ],
          },
        ],
        [{ label: "Notifications", to: "/admin/notifications" }],
      ),
      article(
        "registration-payments",
        "Registration payments",
        "Stripe/Revolut checkout on public registration.",
        [
          {
            heading: "Payments panel",
            body: [
              "Connect payment provider keys under Settings → Registration pay.",
              "Paid tiers require a successful gateway configuration before going live.",
            ],
          },
        ],
        [{ label: "Settings", to: "/admin/settings" }],
      ),
    ],
  },
];

/** Flat list for search */
export function flattenHelpArticles(categories = HELP_CATEGORIES) {
  const out = [];
  for (const cat of categories) {
    for (const art of cat.articles || []) {
      out.push({ ...art, categoryId: cat.id, categoryTitle: cat.title });
    }
  }
  return out;
}

export function searchHelpArticles(query, categories = HELP_CATEGORIES) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return flattenHelpArticles(categories);
  return flattenHelpArticles(categories).filter((art) => {
    const hay = [
      art.title,
      art.summary,
      art.categoryTitle,
      ...(art.sections || []).flatMap((s) => [s.heading, ...(s.body || [])]),
    ]
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });
}
